import { parseToRgb, rgba } from 'polished';
import { uniqBy } from 'lodash';
import styled, { createGlobalStyle } from 'styled-components';
import type { VisualDiffClassName } from '@ardoq/common-helpers';
import { getLightenedColor } from './utils';

export const OffScreenSvg = styled.svg`
  position: absolute;
  left: -1000px;
  top: -1000px;
  width: 0;
  height: 0;
`;
// Tool to create the filters https://ardoq.github.io/svg-filter-tool/src/index.html
// repo https://github.com/ardoq/svg-filter-tool
// online https://ardoq.github.io/svg-filter-tool/src/index.html
// MDN <feColorMatrix> https://developer.mozilla.org/en-US/docs/Web/SVG/Element/feColorMatrix
export const GraphVisualizationFilters = () => (
  <OffScreenSvg>
    <defs>
      <GraphVisualizationFilterElements />
    </defs>
  </OffScreenSvg>
);

type ColorMatrixRow = [number, number, number, number, number];
type ColorMatrix = [
  ColorMatrixRow,
  ColorMatrixRow,
  ColorMatrixRow,
  ColorMatrixRow,
];

const clampByte = (byte: number) =>
  Math.min(255, Math.max(0, Math.round(byte)));

const applyMatrix = (color: string, matrix: ColorMatrix) => {
  const { red, green, blue, alpha } = { alpha: 1, ...parseToRgb(color) };
  const [
    [r1, r2, r3, r4, r5],
    [g1, g2, g3, g4, g5],
    [b1, b2, b3, b4, b5],
    [a1, a2, a3, a4, a5],
  ] = matrix;
  const alphaAsByte = alpha * 255;
  return rgba(
    clampByte(r1 * red + r2 * green + r3 * blue + r4 * alphaAsByte + r5),
    clampByte(g1 * red + g2 * green + g3 * blue + g4 * alphaAsByte + g5),
    clampByte(b1 * red + b2 * green + b3 * blue + b4 * alphaAsByte + b5),
    Math.min(
      1,
      Math.max(
        0,
        Math.round(a1 * red + a2 * green + a3 * blue + a4 * alpha + a5)
      )
    )
  );
};

export const VISUAL_DIFF_FOREGROUND = '#404040';
export const VISUAL_DIFF_BACKGROUND = '#e6e6e6';
const UNCHANGED_MATRIX: ColorMatrix = [
  [0.299, 0.587, 0.114, 0.467, 0.0],
  [0.299, 0.587, 0.114, 0.525, 0.0],
  [0.299, 0.587, 0.114, 0.612, 0.0],
  [0.0, 0.0, 0.0, 1.0, 0.0],
];
const visualDiffFilterMatrices: Record<VisualDiffClassName, ColorMatrix> = {
  'visual-diff-added': [
    [0.206, 0.405, 0.079, 0.298, 0.0],
    [0.206, 0.405, 0.079, 0.714, 0.0],
    [0.206, 0.405, 0.079, 0.314, 0.0],
    [0.0, 0.0, 0.0, 1.0, 0.0],
  ],
  'visual-diff-changed': [
    [0.182, 0.358, 0.07, 0.082, 0.0],
    [0.182, 0.358, 0.07, 0.455, 0.0],
    [0.182, 0.358, 0.07, 1.0, 0.0],
    [0.0, 0.0, 0.0, 1.0, 0.0],
  ],
  'visual-diff-removed': [
    [0.23, 0.452, 0.088, 1.0, 0.0],
    [0.23, 0.452, 0.088, 0.082, 0.0],
    [0.23, 0.452, 0.088, 0.082, 0.0],
    [0.0, 0.0, 0.0, 1.0, 0.0],
  ],
  'visual-diff-unchanged': UNCHANGED_MATRIX,
  'visual-diff-placeholder': UNCHANGED_MATRIX,
};

export const visualDiffColors = Object.fromEntries(
  Object.entries(visualDiffFilterMatrices).map(
    ([visualDiffClassName, matrix]) => [
      visualDiffClassName,
      applyMatrix(VISUAL_DIFF_FOREGROUND, matrix),
    ]
  )
) as Record<VisualDiffClassName, string>;
export const visualDiffBackgroundColors = Object.fromEntries(
  Object.entries(visualDiffColors).map(([visualDiffClassName, foreground]) => [
    visualDiffClassName,
    getLightenedColor(foreground),
  ])
) as Record<VisualDiffClassName, string>;

type VisualDiffFilterId = 'added' | 'changed' | 'removed' | 'unchanged';

const visualDiffFilterIds: Record<VisualDiffClassName, VisualDiffFilterId> = {
  'visual-diff-added': 'added',
  'visual-diff-changed': 'changed',
  'visual-diff-removed': 'removed',
  'visual-diff-unchanged': 'unchanged',
  'visual-diff-placeholder': 'unchanged',
};

const visualDiffFilterEntries = Object.entries(visualDiffFilterIds) as [
  VisualDiffClassName,
  VisualDiffFilterId,
][];

const visualDiffFilterBindings = visualDiffFilterEntries
  .map(
    ([className, filterId]) =>
      `.${className} { filter: url(#visual-diff-filter-${filterId}); }`
  )
  .join('\n');

const VisualDiffFilterGlobalStyle = createGlobalStyle`
  ${visualDiffFilterBindings}
`;

export const GraphVisualizationFilterElements = () => (
  <>
    <VisualDiffFilterGlobalStyle />
    {uniqBy(visualDiffFilterEntries, ([, filterId]) => filterId).map(
      ([className, filterId]) => (
        <filter key={filterId} id={`visual-diff-filter-${filterId}`}>
          <feColorMatrix
            type="matrix"
            values={`${visualDiffFilterMatrices[className]}`}
          />
        </filter>
      )
    )}
  </>
);
